import { useCurrentUser } from "@common/hooks";
import { ExpenseFormValues } from "../forms/Forms";
import { FullPageLoader } from "@common/components/Loading/FullPageLoading";
import { NoResultsCard } from "@common/components";
import { Lightning } from "@phosphor-icons/react";
import { useOrganizationLazyQuery } from "@common/utils/graphql";
import { Button } from "@hypercard/web-components";
import { useToast } from "@/components/ui/use-toast";
import { HypercardTransactionSelection } from "./HypercardTransactionSelection";
import { useRecoilValue } from "recoil";
import {
  impersonatableUserState,
  isImpersonatingState,
} from "@common/state/impersonsonatingUser";

type Props = {
  onSelection: (tx: Partial<ExpenseFormValues>) => void;
};

export const SelectFromHypercardContent = ({ onSelection }: Props) => {
  const isImpersonating = useRecoilValue(isImpersonatingState);
  const { impersonatingUser } = useRecoilValue(impersonatableUserState);
  const { maybeCurrentUser, currentUserLoading } = useCurrentUser({
    skip: isImpersonating,
  });
  const [organizationQuery, { loading: organizationLoading }] =
    useOrganizationLazyQuery();

  const { toast } = useToast();

  if (currentUserLoading) {
    return (
      <div className="h-full">
        <FullPageLoader />
      </div>
    );
  }

  const isCardholder =
    maybeCurrentUser?.cardholder || impersonatingUser?.cardholder;

  // If not a cardholder show the claim your card card
  if (!isCardholder) {
    return (
      <NoResultsCard
        Icon={Lightning}
        header="Claim Your Card"
        body="With Hypercard, you can submit expenses with a single click, earn up to 3X points, and get your reimbursements immediately."
        cardClassName="p-10 mx-4"
      >
        <Button
          className="mt-8 w-[200px]"
          onClick={async () => {
            const { data } = await organizationQuery();

            if (data?.organization.__typename === "QueryOrganizationSuccess") {
              const cardApplicationPageUrl =
                data.organization.data.cardApplicationPageUrl;
              window.open(cardApplicationPageUrl, "_blank");
            } else {
              console.error("Failed to fetch organization");
              toast({
                variant: "destructive",
                title:
                  "There was an error loading your application link, please reach out to support@hypercard.com for help.",
              });
            }
          }}
          size="sm"
          variant="primary"
          loading={organizationLoading}
        >
          Claim Your Card
        </Button>
      </NoResultsCard>
    );
  }

  // If they are a cardholder, allow them to select from their transactions
  return <HypercardTransactionSelection onSelection={onSelection} />;
};
